import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { AdasBox, AdasTypography } from '@components/wrapper-components'
import { Paperclip, Upload, X } from 'lucide-react'
import { MIME_TYPES_MAP } from 'variable-maps'
import { getAttachmentAppointmentEstimate, getAttachmentAppointmentCalibration } from 'api/api'
import { useCenterStore } from '@caradasstore/CenterStore'
import { Logger } from 'logger'
import { cn } from 'lib/utils'

const generateFileUrl = (fileData) => {
  const extIndex = fileData.name.lastIndexOf('.')
  const fileExtension = fileData.name.slice(extIndex)
  const fileBlob = new Blob([fileData], {
    type: MIME_TYPES_MAP[fileExtension.toLowerCase()] || 'application/octet-stream'
  })
  return URL.createObjectURL(fileBlob)
}

export const AppointmentAttachment = ({ name, value, onChange, label, description, onDelete, isViewOnly = false, disabled = false }) => {
  const ccId = useCenterStore((store) => store.ccId)
  const MIME_TYPES = Object.keys(MIME_TYPES_MAP).join(',')
  const [fileURL, setFileURL] = useState('')

  const isFileFromResponse =
    value && typeof value === 'object' && value?.id && !(value instanceof File) && (name === 'recentEstimate' || name === 'calibrationFile')

  useEffect(() => {
    const fetchFileData = async () => {
      try {
        let response
        if (name === 'recentEstimate') {
          response = await getAttachmentAppointmentEstimate(ccId, value.appointment_id)
        } else if (name === 'calibrationFile') {
          response = await getAttachmentAppointmentCalibration(ccId, value.appointment_id)
        } else {
          return
        }
        const fileFromResponse = new File([response.data], value.name)
        fileFromResponse.id = value.id
        const url = generateFileUrl(fileFromResponse)
        setFileURL(url)
      } catch (error) {
        Logger.error({ message: error.message, payload: { value } })
      }
    }

    if (isFileFromResponse) {
      fetchFileData()
    }
    return () => {
      if (fileURL) URL.revokeObjectURL(fileURL)
    }
  }, [value?.id, name])

  const hasFile = isFileFromResponse || value instanceof File
  const fileName = value instanceof File ? value.name : value?.name || ''
  const fileSizeMb = value instanceof File ? `${(value.size / 1024 / 1024).toFixed(2)} MB` : ''
  const downloadHref = value instanceof File ? URL.createObjectURL(value) : fileURL

  if (!hasFile) {
    return (
      <label
        className={cn(
          'flex items-center justify-between gap-4 rounded-xl border border-[#D1D5D8] bg-white px-4 py-3 transition-colors',
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-[#F8FAFC]'
        )}
      >
        <AdasBox className='flex items-center gap-3 min-w-0'>
          <AdasBox className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#E8EFFF]'>
            <Upload className='h-5 w-5 text-[#0A1628]' strokeWidth={1.75} />
          </AdasBox>
          <AdasBox className='min-w-0'>
            <AdasTypography className='text-[15px] font-bold text-[#0A1628]'>{label}</AdasTypography>
            {description && <AdasTypography className='mt-0.5 text-[13px] text-[#6B7280]'>{description}</AdasTypography>}
          </AdasBox>
        </AdasBox>
        <span
          className={cn(
            'shrink-0 rounded-full bg-[#1F6FEB] px-5 py-2 text-[12px] font-bold uppercase tracking-wide text-white transition-colors',
            !disabled && 'hover:bg-[#1A5FD0]'
          )}
        >
          Upload
        </span>
        <input type='file' hidden name={name} id={`upload-${name}`} accept={MIME_TYPES} disabled={disabled} onChange={onChange} />
      </label>
    )
  }

  return (
    <AdasBox className='flex items-center justify-between gap-4 rounded-xl border border-[#D1D5D8] bg-white px-4 py-3'>
      <AdasBox className='flex items-center gap-3 min-w-0'>
        <AdasBox className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#E8EFFF]'>
          <Paperclip className='h-5 w-5 text-[#1F6FEB]' strokeWidth={1.75} />
        </AdasBox>
        <AdasBox className='min-w-0'>
          <AdasTypography className='text-[15px] font-bold text-[#0A1628]'>{label}</AdasTypography>
          <a
            href={downloadHref}
            target='_blank'
            rel='noopener noreferrer'
            download={fileName || undefined}
            onClick={(e) => {
              if (!downloadHref) e.preventDefault()
            }}
            title={fileName}
            className='mt-0.5 block truncate text-[13px] text-[#6B7280] hover:underline'
          >
            {fileName}
            {fileSizeMb && <span className='ml-2 font-mono text-[12px]'>{fileSizeMb}</span>}
          </a>
        </AdasBox>
      </AdasBox>
      <AdasBox className='flex shrink-0 items-center gap-2'>
        {!isViewOnly && (
          <label
            className={cn(
              'inline-flex h-9 px-3 items-center justify-center rounded-lg text-[12px] font-semibold text-[#1F6FEB] hover:bg-[#F1F4F8]',
              disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            )}
          >
            Change
            <input type='file' hidden name={name} id={`change-${name}`} accept={MIME_TYPES} disabled={disabled} onChange={onChange} />
          </label>
        )}
        {!isViewOnly && onDelete && (
          <button
            type='button'
            onClick={onDelete}
            disabled={disabled}
            aria-label='Remove file'
            className='inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#475569] hover:bg-[#F1F4F8] disabled:opacity-40 disabled:cursor-not-allowed'
          >
            <X className='h-4 w-4' />
          </button>
        )}
      </AdasBox>
    </AdasBox>
  )
}

AppointmentAttachment.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  onDelete: PropTypes.func,
  isViewOnly: PropTypes.bool,
  disabled: PropTypes.bool
}
